import React, { useState } from 'react';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { List, Col, Table as TableAntd, Button as ButtonAntd, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import Box from '~/components/Box';
import Row from '~/components/Row';
import './styles.css';
import ObsModal from './obsModal';
import { tableColumns, tableColumnsDetails, tableColumnsDetailsExpanded, tableSummary } from './tableColumns';
import { usePriceQuotesData } from './usePriceQuotesData';

export default function PriceQuotes({ project, customer }) {
  const {
    loading,
    completeEstimate,
    laborTypes,
    selectedEstimateSubVendors,
    estimateData,
    winners,
    showType,
    setShowType,
    handleWinner,
    handleSaveWinners,
  } = usePriceQuotesData(project.id);

  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});

  // calc final date from given workdays number
  const calcFinalDate = workdays => {
    const startDate = estimateData?.sent;
    var moment = require('moment-business-days');
    moment.updateLocale('en', { workingWeekdays: [1, 2, 3, 4, 5] });
    const finalDate = moment(startDate, 'YYYY-MM-DD')
      .businessAdd(workdays)
      .format('YYYY-MM-DD');
    return finalDate;
  };

  const handleNoteModal = record => {
    setSelectedRecord(record);
    setVisible(true);
  };

  return (
    <Spin spinning={loading} tip="Loading...">
      <Box>
        <Row>
          <Col span={4}>
            <List
              header={t('screens:price_quotes.quoted_services')}
              footer={
                <ButtonAntd
                  block
                  type="primary"
                  disabled={winners.length < laborTypes.length}
                  onClick={() => handleSaveWinners()}
                >
                  {t('screens:price_quotes.save_winners')}
                </ButtonAntd>
              }
              bordered
              dataSource={laborTypes}
              renderItem={item => (
                <List.Item>
                  <ButtonAntd
                    icon={
                      winners.find(winner => winner.laborTypeId === item.id) ? (
                        <CheckCircleOutlined />
                      ) : (
                        <ExclamationCircleOutlined />
                      )
                    }
                    style={
                      winners.find(winner => winner.laborTypeId === item.id)
                        ? { color: '#22cc22' }
                        : { color: '#1890ff' }
                    }
                    type="link"
                    onClick={() => setShowType(item.id)}
                  >
                    <span style={{ fontWeight: showType === item.id ? 'bold' : 'normal' }}>{item.name}</span>
                  </ButtonAntd>
                </List.Item>
              )}
            />
          </Col>
          <Col span={20}>
            <Row gutter={[0, 20]}>
              <Col span={24}>
                <p style={{ paddingBottom: 5 }}>{laborTypes.find(laborType => laborType.id === showType)?.name}</p>
              </Col>
            </Row>
            <TableAntd
              bordered
              rowClassName={record =>
                winners.find(winner => winner.laborTypeId === showType && winner.subVendorId === record.subVendor.id)
                  ? 'table-parent-row-winner'
                  : 'table-parent-row'
              }
              rowKey={record => record.subVendor.id}
              pagination={false}
              loading={loading}
              columns={tableColumns(t, showType, winners, handleWinner, calcFinalDate)}
              dataSource={completeEstimate}
              showHeader={true}
              expandable={{
                expandedRowRender: record => (
                  <TableAntd
                    rowClassName={record => (record.laborType.id === showType ? 'table-row' : 'table-row-hidden')}
                    rowKey="id"
                    pagination={false}
                    loading={loading}
                    columns={tableColumnsDetails(t, handleNoteModal)}
                    expandable={{
                      expandedRowRender: record => (
                        <TableAntd
                          bordered
                          rowClassName="table-row-detail-expanded"
                          rowKey="id"
                          pagination={false}
                          loading={loading}
                          columns={tableColumnsDetailsExpanded(t)}
                          dataSource={[record]}
                          showHeader={false}
                          size="small"
                        />
                      ),
                    }}
                    dataSource={record.items}
                    showHeader={true}
                    size="small"
                    title={() => t('screens:price_quotes.items.details')}
                    summary={tableSummary(t, selectedEstimateSubVendors, record.subVendor.id, showType, loading)}
                  />
                ),
                rowExpandable: record => record.name !== 'Not Expandable',
              }}
            />
          </Col>
        </Row>
      </Box>
      <ObsModal
        visible={visible}
        onCancel={() => setVisible(false) && setSelectedRecord(null)}
        selectedRecord={selectedRecord}
      />
    </Spin>
  );
}
